import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSimpleSnapshot } from '../../hooks/useSimpleSnapshot';
import { useSnapshotScope } from '../../contexts/SnapshotScope';
import { useUser } from '../../contexts/UserContext';
import { useGridNav } from '../../hooks/useGridNav';
import { Input, ConfirmationModal } from '../../components/shared/ui';
import { Trash2, RefreshCw } from 'lucide-react';
import { getCurrentWeekKey, getWeekStartDateFromKey } from '../../utils/weekKeyUtils';
import { ALLOW_PAST_WEEK_EDITS } from '../../config/auth';

import SectionHeader from '../../components/shared/ui/SectionHeader';
import SectionContainer from '../../components/shared/ui/SectionContainer';
import VirtualizedSpendTable, { SortOrderItem, SpendTableColumn } from '../../components/shared/ui/VirtualizedSpendTable';
import { LockedTableHeader } from '../../components/shared/ui/LockedTableHeader';
import { LockedInputRow } from '../../components/shared/ui/LockedInputRow';
import ExportButton from '../../components/shared/ui/ExportButton';
import { formatTableDataForExport } from '../../utils/exportUtils';
import { LoadErrorState } from '../../components/shared/ui/LoadErrorState';


// Helper to get column alignment
const getColumnAlign = (columnKey: string): 'left' | 'center' | 'right' => {
  switch (columnKey) {
    case 'creator':
      return 'left';
    case 'bubbleAI':
    case 'bubbleMobile':
    case 'platform':
    case 'actions':
    default:
      return 'center';
  }
};

const columns = [
  { key: 'creator', label: 'Creator Name', sortable: true, align: getColumnAlign('creator') },
  { key: 'bubbleAI', label: 'Bubble AI', sortable: true, align: getColumnAlign('bubbleAI') },
  { key: 'bubbleMobile', label: 'Bubble Native Mobile', sortable: true, align: getColumnAlign('bubbleMobile') },
  { key: 'platform', label: 'Platform', sortable: true, align: getColumnAlign('platform') }
];

type PromoCodeRow = {
  id: number;
  creator: string;
  bubbleAI: string;
  bubbleMobile: string;
  platform: string;
  deleted?: boolean; // Forward-only deletion: deleted items don't forward-fill to future weeks
  actions?: number; // Optional actions field for table rendering
};


const PromoCodesSection: React.FC = () => {
  // User context available if needed
  const { workspaceId, periodKey } = useSnapshotScope();
  
  // Use the simplified useSimpleSnapshot hook
  const { 
    data: rows, 
    save, 
    rootKey: sectionRootKey,
    isLoading,
    workspaceReady,
    loadError
  } = useSimpleSnapshot<PromoCodeRow[]>({
    workspaceId,
    section: 'promoCodes',
    periodKey,
    defaultValue: []
  });
  
  // For Promo Codes, both Admins and Viewers can edit regardless of grace period
  // Use standardized permission check for consistency
  // Use camelCase to match snapshot key format
  const { canEditSection, isActuallyAdmin } = useUser();
  const isAdmin = isActuallyAdmin; // Check actual role, not effective role (viewer mode)
  const canEdit = canEditSection('promoCodes');
  
  // Check if current week, future week, or past week editing is allowed
  const currentWeekKey = getCurrentWeekKey();
  const currentWeekDate = getWeekStartDateFromKey(currentWeekKey);
  const periodDate = getWeekStartDateFromKey(periodKey);
  const isCurrentWeek = periodKey === currentWeekKey;
  const isFutureWeek = currentWeekDate && periodDate && periodDate.getTime() > currentWeekDate.getTime();
  const isPastWeek = currentWeekDate && periodDate && periodDate.getTime() < currentWeekDate.getTime();
  
  // Admins can always edit past weeks (for cumulative dashboard, mistake corrections, etc.)
  // Viewers can only edit current/future weeks (or past weeks if explicitly enabled)
  // Promo Codes is special - both admins and viewers can edit, but admins have more flexibility
  const isEditable = canEdit && (isCurrentWeek || isFutureWeek || (isPastWeek && (isAdmin || ALLOW_PAST_WEEK_EDITS)));

  const hasAnyPromoInput = (r: PromoCodeRow) =>
    (r.creator?.trim() ?? '') !== '' ||
    (r.platform?.trim() ?? '') !== '' ||
    (r.bubbleAI?.trim() ?? '') !== '' ||
    (r.bubbleMobile?.trim() ?? '') !== '';

  // Permanent input bar (visible to all users who can edit)
  const [inputRow, setInputRow] = useState<PromoCodeRow>({
    id: -1, // Negative ID to distinguish from saved rows
    creator: '',
    bubbleAI: '',
    bubbleMobile: '',
    platform: ''
  });

  // Show skeleton only for data area during initial load (input row always visible)
  // ✅ FIX: Check for undefined explicitly (empty array [] is valid data)
  // ✅ FIX: Check workspaceReady for consistency with NotesSection
  const showDataSkeleton = !workspaceReady || (isLoading && rows === undefined);

  // Ensure there's always saved rows array, filtering out deleted items for display
  // Forward-only deletion: deleted items are preserved in data but hidden from UI
  const safeRows = useMemo(() => {
    const baseRows = Array.isArray(rows) ? rows : [];
    return baseRows.filter((row: PromoCodeRow) => !row.deleted);
  }, [rows]);

  // Show cached data with skeleton overlay during refresh
  const hasCachedData = rows !== undefined;
  const isRefreshing = isLoading && hasCachedData;
  
  // Auto-save is handled by useSimpleSnapshot hook with debouncing
  
  // Immediate saving to maintain data consistency
  
  // Keyboard navigation setup
  const rowCount = safeRows.length;
  const cols = 4; // creator, bubbleAI, bubbleMobile, platform
  const { onKeyDown } = useGridNav('promo-codes');

  
  
  // State for table collapse and delete confirmation
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; rowId: number | null; rowData: PromoCodeRow | null }>({
    show: false,
    rowId: null,
    rowData: null
  });
  
  // Sorting state
  const [sortOrder, setSortOrder] = useState<SortOrderItem[]>([]);

  // Log when week changes
  useEffect(() => {
  }, [periodKey]);

  const handleCellChange = useCallback((rowIdx: number, columnKey: string, value: string) => {
    if (!isEditable) return;
    
    const safePrevRows = Array.isArray(rows) ? rows : [];
    const newRows = [...safePrevRows];
    if (newRows[rowIdx]) {
      newRows[rowIdx] = { ...newRows[rowIdx], [columnKey]: value };
    }
    save(newRows);
  }, [isEditable, rows, save]);

  // Handle adding new row
  // Handle input bar submission - create new row from input bar
  const handleInputBarSubmit = useCallback(() => {
    if (!isEditable) return;

    if (!hasAnyPromoInput(inputRow)) return; // avoid truly blank adds

    const newRow: PromoCodeRow = { ...inputRow, id: Date.now() };
    const newRows = [...(Array.isArray(safeRows) ? safeRows : []), newRow];
    save(newRows);

    setInputRow({ id: -1, creator: '', bubbleAI: '', bubbleMobile: '', platform: '' });
  }, [isEditable, inputRow, safeRows, save]);
  
  // Handle input bar field changes
  const handleInputBarChange = useCallback((field: keyof PromoCodeRow, value: string) => {
    setInputRow(prev => ({ ...prev, [field]: value }));
  }, []);
  

  // Handle deleting row
  const handleDeleteRow = useCallback((rowId: number) => {
    
    if (!isEditable) {
      return;
    }
    
    const rowToDelete = safeRows.find((row: PromoCodeRow) => row.id === rowId);
    
    if (rowToDelete) {
      setDeleteConfirm({
        show: true,
        rowId: rowId,
        rowData: rowToDelete
      });
    } else {
    }
  }, [canEdit, rows]);

  // Confirm delete
  const confirmDelete = useCallback(() => {
    if (deleteConfirm.rowId !== null) {
      // Mark as deleted instead of removing from array (forward-only deletion)
      // This preserves historical data in past weeks while preventing forward-fill to future weeks
      const updated = (rows || []).map((row: PromoCodeRow) =>
        row.id === deleteConfirm.rowId
          ? { ...row, deleted: true }
          : row
      );
      save(updated);
      setDeleteConfirm({ show: false, rowId: null, rowData: null });
    }
  }, [deleteConfirm.rowId, rows, save]);

  // Cancel delete
  const cancelDelete = useCallback(() => {
    setDeleteConfirm({ show: false, rowId: null, rowData: null });
  }, []);

  // Keyboard shortcuts for delete confirmation modal
  useEffect(() => {
    if (!deleteConfirm.show) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        confirmDelete();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancelDelete();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [deleteConfirm.show, confirmDelete, cancelDelete]);

  // Input refs for focus management
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Ensure refs array is correct size
  useEffect(() => {
    inputRefs.current = {};
    safeRows.forEach((_: PromoCodeRow, rowIdx: number) => {
      columns.forEach((_column: SpendTableColumn<PromoCodeRow>, colIdx: number) => {
        const key = `${rowIdx}-${colIdx}`;
        inputRefs.current[key] = null;
      });
    });
  }, [rows]);


  // Custom renderCell function for VirtualizedSpendTable
  const renderCell = useCallback((row: PromoCodeRow, columnKey: string, rowIdx?: number, colIdx?: number) => {
    const isInputBar = row.id === -1; // Check if this is the permanent input bar
    
    // Handle actions column first (this should always work)
    if (columnKey === 'actions') {
      const isInputBar = row.id === -1;

      if (isInputBar) {
        return (
          <div className="flex items-center justify-center gap-2 w-full">
            <button
              onClick={handleInputBarSubmit}
                className="px-3 py-1.5 text-sm font-medium text-textInverse bg-accent hover:bg-accentStrong rounded-lg transition-colors"
            >
              Add
            </button>
            <button
              onClick={() => setInputRow({ id: -1, creator: '', bubbleAI: '', bubbleMobile: '', platform: '' })}
                className="px-3 py-1.5 text-sm font-medium text-textPrimary bg-card border border-border rounded-lg hover:bg-tableHover transition-colors"
            >
              Clear
            </button>
          </div>
        );
      }

      // saved row — delete button
      return (
        <div className="flex items-center justify-center w-full">
          <button
            className="text-textPrimary hover:text-danger p-3 rounded-lg hover:bg-[color-mix(in_srgb,var(--color-danger) 12%, transparent)] transition-all duration-200"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeleteRow(row.id); }}
            title="Delete promo code"
            data-testid={`delete-row-btn-${row.id}`}
          >
            <Trash2 size={18} />
          </button>
        </div>
      );
    }
    
    // For other columns, check if we have valid row/col indices
    if (rowIdx === undefined || colIdx === undefined) return row[columnKey as keyof PromoCodeRow];
    
    const col = columns.find(c => c.key === columnKey);
    if (!col) return row[columnKey as keyof PromoCodeRow];

    // Handle code columns with pill styling for saved rows
    const codeValue = row[columnKey as keyof PromoCodeRow] as string;
    
    // Show bubble styling for all columns when they have values
    if (!isInputBar && codeValue) {
      const getBubbleClass = (colKey: string) => {
        switch (colKey) {
          case 'creator':
            return 'mgz-pill--creator';
          case 'bubbleAI':
            return 'mgz-pill--ai';
          case 'bubbleMobile':
            return 'mgz-pill--mobile';
          case 'platform':
            return 'mgz-pill--platform';
          default:
            return '';
        }
      };

      return (
        <div className="flex items-center justify-center gap-2">
          <code className={`mgz-pill ${getBubbleClass(columnKey)}`}>
            {codeValue}
          </code>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center w-full" data-row={rowIdx} data-col={colIdx}>
        <Input
          ref={el => {
            const key = `${rowIdx}-${colIdx}`;
            inputRefs.current[key] = el;
          }}
          data-row={rowIdx}
          data-col={colIdx}
          tabIndex={0}
          style={{ minWidth: '12rem', maxWidth: 'none' }}
          value={row[columnKey as keyof PromoCodeRow] as string ?? ''}
          placeholder={`Enter ${col.label.toLowerCase()}`}
          className={`w-full min-w-0 px-4 py-3 rounded-lg input-surface text-textPrimary placeholder:text-textMuted border border-border text-sm text-center
            hover:border-border
            ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}
            focus:outline-none focus:ring-2 focus:ring-[var(--focus-ring)] focus:border-[var(--focus-ring)] transition-colors duration-200
          `}
          onChange={e => {
            if (isInputBar) {
              handleInputBarChange(columnKey as keyof PromoCodeRow, e.target.value);
            } else {
              handleCellChange(rowIdx, columnKey, e.target.value);
            }
          }}
          onBlur={() => { /* no auto submit for input row */ }}
          onKeyDown={e => {
            if (isInputBar && e.key === 'Enter') {
              e.preventDefault();
              handleInputBarSubmit();
            } else {
              onKeyDown(e, rowIdx, colIdx, rowCount - 1, cols - 1);
            }
          }}
            disabled={!isEditable}
          data-testid={`${columnKey}-input`}
        />
      </div>
    );
  }, [canEdit, handleCellChange, onKeyDown, rowCount, cols, handleDeleteRow, handleInputBarChange, handleInputBarSubmit, inputRow]);

  // Prepare data for VirtualizedSpendTable with actions column
  const columnsWithActions = useMemo(() => (
    canEdit ? [...columns, { key: 'actions', label: 'Actions', sortable: false, align: getColumnAlign('actions') }] : columns
  ), [canEdit, columns]);

  const sortedRows = useMemo(() => {
    if (!Array.isArray(safeRows) || sortOrder.length === 0) return safeRows || [];

    const [{ field, direction }] = sortOrder;
    const sorted = [...(safeRows || [])].sort((a, b) => {
      const av = (a as Record<string, unknown>)[field];
      const bv = (b as Record<string, unknown>)[field];
      if (av === bv) return 0;
      if (av === undefined || av === null) return direction === 'asc' ? -1 : 1;
      if (bv === undefined || bv === null) return direction === 'asc' ? 1 : -1;
      return direction === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return sorted;
  }, [safeRows, sortOrder]);

  // Prepare saved rows with actions column (input row rendered separately)
  const savedRowsWithActions = useMemo(() => {
    const dataWithActions = canEdit
      ? sortedRows.map((row) => ({ ...row, actions: row.id }))
      : sortedRows;
    return dataWithActions || [];
  }, [sortedRows, canEdit]);

  const exportData = useMemo(() => {
    const data = formatTableDataForExport(sortedRows || [], columns);
    if (sortOrder.length > 0) {
      const sortMeta = sortOrder.map(order => {
        const column = columns.find(col => col.key === order.field);
        const label = column ? column.label : order.field;
        return `${label}: ${order.direction.toUpperCase()}`;
      });
      data.metadata = {
        ...data.metadata,
        sortOrder: sortMeta
      };
    }
    return data;
  }, [sortedRows, sortOrder]);

  const handleResetSort = useCallback(() => {
    setSortOrder([]);
  }, []);

  const isSorted = sortOrder.length > 0;

  // Custom getTopItems for saved rows (input row not included)
  const getTopItems = useCallback((data: PromoCodeRow[], count: number) => {
    if (!isCollapsed) return data;
    return data.slice(0, count);
  }, [isCollapsed]);


  return (
    <SectionContainer 
      key={sectionRootKey}
      testId="section-promo-codes" 
      aria-label="Promo Codes section"
      variant="bare"
      data-grid="promo-codes"
      className={`${hasCachedData ? 'animate-fadeIn' : ''} relative`}
    >
      {/* Error state if workspace failed to resolve */}
      {loadError && (
        <div className="px-6 py-6">
          <LoadErrorState
            error={loadError}
            onRetry={() => window.location.reload()}
            onRefresh={() => window.location.reload()}
            sectionName="Promo Codes"
          />
        </div>
      )}
      {/* Header always visible - outside skeleton wrapper */}
      {!loadError && (
        <div className="w-full">
        <div className="bg-[var(--bg-elev)] rounded-[1.75rem] overflow-hidden overflow-x-hidden" data-section="promo-codes">
          {/* Header Section - Always rendered */}
          <div className="px-6 py-6 card-header">
            <SectionHeader title="Promo Codes" href="https://docs.google.com/promo-codes-master">
              {/* Action Buttons */}
              <div className="flex items-center gap-3">
                <ExportButton
                  data={exportData}
                  filename={`promo_codes_${periodKey ?? 'export'}`}
                  disabled={sortedRows.length === 0}
                  variant="csv"
                  showDropdown={false}
                  className="mr-2 bg-accent text-textInverse hover:bg-accentStrong"
                />
                <button
                  onClick={handleResetSort}
                  disabled={!isSorted}
                  className={`clickable-action-btn px-3 py-2 text-sm font-medium text-textPrimary bg-card border border-border rounded-lg hover:bg-tableHover transition-all duration-200 inline-flex items-center gap-2 ${
                    isSorted ? '' : 'opacity-60 cursor-not-allowed hover:bg-transparent'
                  }`}
                  aria-label="Reset promo code sort"
                >
                  <RefreshCw size={14} />
                  Reset Sort
                </button>
                <button
                  onClick={() => setIsCollapsed(!isCollapsed)}
                  disabled={(rows?.length ?? 0) <= 5}
                  className={`clickable-show-more px-3 py-2 text-sm font-medium border theme-border rounded-lg transition-all duration-200 ${
                    (rows?.length ?? 0) <= 5
                      ? 'theme-text-muted theme-muted cursor-not-allowed'
                      : 'theme-text-light theme-surface hover:bg-[color-mix(in_srgb,var(--accent-primary)_12%,_transparent)] hover:theme-border cursor-pointer'
                  }`}
                  aria-label={isCollapsed ? 'Show more promo codes' : 'Show fewer promo codes'}
                >
                  {isCollapsed ? 'Show More' : 'Show Less'}
                </button>
              </div>
            </SectionHeader>
          </div>

          {/* Table Section - Locked Header, Locked Input Row, Scrollable Data Rows */}
          <div className="px-4 py-3">
            {/* 1. LOCKED TABLE HEADER - Always visible, not affected by sorting/scrolling */}
            <LockedTableHeader
              columns={columnsWithActions}
              sortOrder={sortOrder}
              onSort={(field, event) => {
                const isShiftClick = event.shiftKey;
                let newOrder: SortOrderItem[];
                if (isShiftClick) {
                  const existingIndex = sortOrder.findIndex(item => item.field === field);
                  if (existingIndex > -1) {
                    const existingItem = sortOrder[existingIndex];
                    const updatedItem: SortOrderItem = { ...existingItem, direction: existingItem.direction === 'asc' ? 'desc' : 'asc' };
                    newOrder = sortOrder.map((item, index) => index === existingIndex ? updatedItem : item);
                  } else {
                    newOrder = [...sortOrder, { field, direction: 'asc' as const }];
                  }
                } else {
                  const currentSort = sortOrder.find(item => item.field === field);
                  if (currentSort) {
                    newOrder = [{ field, direction: currentSort.direction === 'asc' ? 'desc' : 'asc' }];
                  } else {
                    newOrder = [{ field, direction: 'asc' as const }];
                  }
                }
                setSortOrder(newOrder);
              }}
              variant="magazine"
            />

            {/* 2. LOCKED INPUT ROW - Always visible, not affected by sorting/scrolling/animation */}
            {canEdit && (
              <LockedInputRow
                inputRow={inputRow}
                columns={columnsWithActions}
                renderCell={renderCell}
              />
            )}

            {/* 3. SCROLLABLE DATA CONTAINER - Only data rows scroll, fade in after loading */}
            {/* Fit-to-content: Only use fixed height when virtualization is needed (>20 rows) */}
            <div className="relative" style={savedRowsWithActions.length > 20 ? { height: '400px' } : { minHeight: 'auto' }}>
              {showDataSkeleton ? (
                <div className="min-h-[200px] space-y-2">
                  {[1, 2, 3, 4, 5].map(i => (
                    <div key={i} className="h-[60px] bg-surface rounded animate-pulse"></div>
                  ))}
                </div>
              ) : (
                <>
                  {isRefreshing && (
                    <div className="absolute inset-0 bg-[var(--bg)] bg-opacity-50 z-10 flex items-center justify-center pointer-events-none">
                      <div className="opacity-75">
                        <div className="min-h-[200px] space-y-2">
                          {[1, 2, 3, 4, 5].map(i => (
                            <div key={i} className="h-[60px] bg-surface rounded animate-pulse"></div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Smooth fade-in animation for data rows only (300ms) */}
                  <div className={`transition-opacity duration-300 ease-out ${hasCachedData ? 'opacity-100' : 'opacity-0'}`}>
                    <VirtualizedSpendTable
                      key={sectionRootKey}
                      title=""
                      data={savedRowsWithActions}
                      columns={columnsWithActions}
                      isCollapsed={isCollapsed}
                      showTotals={false}
                      sortOrder={sortOrder}
                      onSortOrderChange={setSortOrder}
                      getTopItems={getTopItems}
                      renderCell={renderCell}
                      rowHeight={60}
                      overscan={5}
                      containerHeight={savedRowsWithActions.length > 20 ? 400 : undefined}
                      variant="magazine"
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      )}
      
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteConfirm.show}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Promo Code"
        message="Are you sure you want to delete this promo code?"
        variant="destructive"
      />
    </SectionContainer>
  );
};
  
export default PromoCodesSection;